import PropTypes from 'prop-types'
import { useState, useRef, useEffect } from 'react'
import UrlBuilder from '../shared/urlBuilder/UrlBuilder'
import { Button, Loading, Modal, TextInput, Link } from 'carbon-components-react'
import LogInAPI from '../api/LogInAPI'
import passwordUtils from '../../utils/inputTextPasswordUtils'
import LogInErrors from '../data/LogInErrors.json'
import { LiveAnnouncer, LiveMessage } from 'react-aria-live'
import ErrorMessageIcon from '../../globals/images/icon-error-message.svg'
import ReactHtmlParser from 'react-html-parser'

export default function ConfirmPasswordModal( props ) {

    const [ passwordError, setPasswordError ] = useState( null )
    const [ password, setPassword ] = useState( '' )
    const [ submitEnabled, setSubmitEnabled ] = useState( false )
    const [ accountBlockedError, setAccountBlockedError ] = useState( '' )
    const [ backendError, setbackendError ] = useState( '' )
    const errorMsg = 'The password is incorrect'
    
    const handlePassword = ( e ) => {
        const maxPasswordLength = 1

        setPassword( e.target.value )
        setPasswordError( '' )

        if( e.target.value?.length < maxPasswordLength ){
            setSubmitEnabled( false )
        }
        else {
            setSubmitEnabled( true )
        }

        passwordUtils.eyeColorValidation( 'current-password' , e.target.value )
    }

    const forgotPasswordUrl= props.forgotPasswordUrl || process.env.REACT_APP_FORGOT_PASSWORD 

    const contactUsUrl = props.contactUsUrl || process.env.REACT_APP_REDIRECT_TO_ABOUT_URL
    
    const logInApiUrl = props.logInApiUrl || process.env.REACT_APP_LOG_IN_API_URL 
   
    const logOffUrl= props.logOffUrl || process.env.REACT_APP_LOGOFF_URL 

    const handleCopy = ( event ) => {
        event.preventDefault()
        return false
    }

    const submit = ( event ) => {
         event.preventDefault()
        if( submitEnabled ) {
             setbackendError( '' )
            props.changeLoading( true )
            LogInAPI( {
                webId: props.webId,
                password: password,
                rememberMe: false,
                apiUrl: logInApiUrl
            } )
            .then( data => {
                if( data.IsSuccess ) {
                    props.onSubmit()
                } 
                else{
                    if( data.Errors ){
                        Object.entries( data.Errors ).map( ( [ key ] ) => {
                            if( key.includes( 'AccountBlocked' ) ){
                                const error = LogInErrors[ key ]
                                const url = UrlBuilder( { url: forgotPasswordUrl, buildURL: true } )
                                setAccountBlockedError( error.replace( '{urlForgotPassword}' , `<a href="${ url }" aria-label=""> reset your password to continue</a>` ) )
                                setTimeout( ( ) => location.assign( UrlBuilder( { url: logOffUrl, buildURL: true } ) ), 600000 )
                            }
                            else{          
                                setPasswordError( errorMsg )
                            }
                        } )
                    }
                }
            } )
            .catch( data => {
                 const error=LogInErrors.BackendError
                 const url = UrlBuilder( { url: contactUsUrl, buildURL: true } )
                 const errorTxt=error.replace( '{contactUs}' , `<a href="${ url }" aria-label="contact us"> contact us</a>` )
                 setbackendError( errorTxt ) 
                  
            } )
            .finally( _ => {
                props.changeLoading( false )
            } )
            setSubmitEnabled( false )
        }
    }

    const inputElement = useRef( null )
     
    useEffect( () => {
        if( props.open && !props.loading )
            inputElement?.current?.focus() 
        if ( password && !props.open ){
            setPassword( '' )
            setPasswordError( '' )
        }  
    }, [ props.open, props.loading, passwordError ] 
    )

    return(
        <Modal
            tabIndex={ 0 }
            open = { props.open }
            passiveModal = { true }
            modalHeading="Enter your current password to update your profile."
            modalLabel="Current Password"
            onRequestClose={ ( e ) => {
                setbackendError( '' )
                if( e[ '_reactName' ] === 'onClick' ) {
                    props.handleModalOpenClose( false )
                    setSubmitEnabled( false )
                }
                if( accountBlockedError ){
                    location.assign( UrlBuilder( { url: logOffUrl, buildURL: true } ) )
                }
            } } 
            >
            {                
                accountBlockedError ?
                    <div className="c-form-group c-modal-space-width">
                        <div className="c-form-error-message c-form-modal-error-message">                                                        
                            <img src={ ErrorMessageIcon } alt="Message Error" className="svg-form-error-message"/>
                            <p tabIndex="0"> { ReactHtmlParser( accountBlockedError ) } </p>
                            <LiveAnnouncer>                      
                                <LiveMessage message= { accountBlockedError } aria-live="polite" />
                            </LiveAnnouncer> 
                        </div>           
                    </div>
                : null
            }
            {                
                backendError ?
                    <div className="c-form-group c-modal-space-width">
                        <div className="c-form-error-message c-form-modal-error-message">                                                        
                            <img src={ ErrorMessageIcon } alt="Message Error" className="svg-form-error-message"/>
                            <p tabIndex="0"> { ReactHtmlParser( backendError ) } </p>
                            <LiveAnnouncer>                      
                                <LiveMessage message= { backendError } aria-live="polite" />
                            </LiveAnnouncer> 
                        </div>           
                    </div>
                : null
            }
            <form onSubmit={ submit } className="c-form-group mb-3">
                <TextInput.PasswordInput
                    data-modal-primary-focus
                    labelText = "Current Password" 
                    id = "current-password"
                    className = "c-form-input"
                    value = { password } 
                    onChange = { handlePassword }                                                                   
                    maxLength = "79"  
                    onCopy = { handleCopy }
                    invalidText = { passwordError }
                    invalid = { passwordError ? true : false }
                    tabIndex = { 0 }
                    aria-label={ passwordError ? passwordError : 'Enter your current password to update your profile.' }
                    ref={ inputElement } 
                    data-testid="input-password"
                    autoComplete="not"
                />
                <Link className="c-form-forgot-pass-link" href={ UrlBuilder( { url: forgotPasswordUrl, buildURL: true } ) }>Forgot your password?</Link>
            </form>
            <div className="modal-footer-center">
                <div className="c-form-group mb-3">
                    {
                        props.loading ?
                            <div className='center-spinner-current-password-wrapper'>
                                <Loading
                                    description= { null } className="center-spinner center-spinner-current-password" withOverlay={ false } small = { true }
                                />                        
                            </div>
                            :
                            <Button   
                                className={ `btn-modal-center ${ submitEnabled ? 'c-btn c-btn--primary' : 'c-btn c-btn--disabled' } mb-5` } 
                                onClick={ submit }
                                type="submit"
                                tabIndex={ 0 }
                                aria-label={ `Submit ${ submitEnabled ? 'Enabled' : 'Disabled' } ` }
                                data-testid="btn-modal-Submit"
                            >
                                Submit
                            </Button>
                    }
                </div>
                <div className="u-text-center mb-3">  
                    <a onClick={ 
                        () => { 
                            props.handleModalOpenClose( false, accountBlockedError ? true : false )
                            setSubmitEnabled( false )
                             setbackendError( '' )
                        }
                    }
                        className="btn-modal-center c-link c-cursor-pointer" 
                        tabIndex={ 0 }                         
                        data-testid="btn-cancel">
                        Cancel
                    </a>
                </div>  
            </div>  
        </Modal>
    )
}

ConfirmPasswordModal.propTypes = {
    open: PropTypes.bool,
    forgotPasswordUrl: PropTypes.string,
    contactUsUrl: PropTypes.string,
    changeLoading: PropTypes.func,
    onSubmit: PropTypes.func,
    loading: PropTypes.bool,
    webId: PropTypes.string,
    logInApiUrl: PropTypes.string,
    handleModalOpenClose: PropTypes.func,
    logOffUrl: PropTypes.string
}